import Debit from '@/models/Debit';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { UNLIMITED_PAYMENTS, getActiveStatus, isUnlimited } from './standingOrderModel';
import type { OrderState } from './standingOrderActions';

/*
 * HAT-1441: freezing, reactivating, editing and cancelling a bank standing order the office keeps in
 * APT, from the bank board - written straight into the order's APT row (Druk, 15.10.2026: directly,
 * as on the credit board, no approval by the office first). The rules are the server's
 * (AptBankOrderPlanner), repeated so the board offers only what the server will do and the form can
 * say what is wrong before anything is sent.
 */

/** Must match FeatureFlagKeys.BankStandingOrderActions and SeedFeatureFlag_BankStandingOrderActions.sql. */
export const BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY = 'bank-standing-order-actions';

/**
 * Where a bank order stands for the board's actions. `stopped` is a stop the office made - its own
 * word in «סטטוס» («ביטול הרשאה», «לבקשת התורם»): only the office changes such an order, whatever
 * else is true of it.
 *
 * A freeze from the board outranks an end date that passed while the order stood frozen: the order
 * still has payments, and reactivating it moves the end so none is lost. Out of payments, it ended.
 */
export type BankOrderState = 'enabled' | 'frozen' | 'cancelled' | 'stopped' | 'ended';

export function getBankOrderState(order: NedarimStandingOrderDtoIn): BankOrderState {
  if (order.aptCancelledFromSite) return 'cancelled';
  if (order.aptStoppedByOffice) return 'stopped';
  if (order.aptFrozenFromSite && !order.aptOutOfPayments) return 'frozen';
  const active = getActiveStatus(order);
  if (active === 'ended') return 'ended';
  if (active === 'disabled') return 'stopped';
  // Awaiting the bank is live for this purpose: the office already sends it, and it can be stopped.
  return 'enabled';
}

/** The blade's status panel speaks the credit states; an office stop has no panel (its alert says it). */
export function toOrderState(state: BankOrderState): OrderState | null {
  switch (state) {
    case 'enabled': return 'enabled';
    case 'frozen': return 'disabled';
    case 'cancelled': return 'cancelled';
    case 'ended': return 'ended';
    default: return null;
  }
}

/*
 * What each state offers - the credit board's rules, except that a cancellation can be undone
 * (Chaya, 19.10.2026: nobody should have to call the office for what the board can put back). As on
 * a credit order, an ended order can be given payments again and can be cancelled; a cancelled one
 * is reactivated before anything else, as long as it has payments left.
 */
export const canFreezeBank = (state: BankOrderState): boolean => state === 'enabled';
export const canReactivateBank = (state: BankOrderState, order?: Pick<NedarimStandingOrderDtoIn, 'aptOutOfPayments'>): boolean =>
  state === 'frozen' || (state === 'cancelled' && !order?.aptOutOfPayments);
export const canEditBank = (state: BankOrderState): boolean => state === 'enabled' || state === 'frozen' || state === 'ended';
export const canCancelBank = (state: BankOrderState): boolean => state === 'enabled' || state === 'frozen' || state === 'ended';

/** The order as it reads once a change is written - the server's answer, so the board need not wait for the Azure copy. */
export interface AptOrderState {
  isActive: boolean;
  status: string | null;
  /** yyyy-MM-dd */
  statusDate: string | null;
  monthlyAmount: number;
  numberOfPayments: number;
  /** yyyy-MM-dd */
  endsAt: string | null;
}

export interface UpdateBankStandingOrderRequest {
  amount?: number;
  remainingPayments?: number;
  unlimitedPayments?: boolean;
}

/**
 * The board's list comes from the Azure copy of APT, which catches up with the office's database a
 * little after the write. Until it does, the row is shown as the server said it now reads - for ten
 * minutes at most, after which the list is taken at its word.
 */
export interface AptOrderOverride {
  state: AptOrderState;
  /** Date.now() when the server answered. */
  at: number;
}

export const OVERRIDE_TTL_MS = 10 * 60 * 1000;

const sameText = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').trim() === (b ?? '').trim();

/** Whether the list already shows what the server wrote. */
export function reflects(debit: Debit, state: AptOrderState): boolean {
  return debit.isActive === state.isActive
    && sameText(debit.status, state.status)
    && debit.monthlyAmount === state.monthlyAmount
    && debit.numberOfPayments === state.numberOfPayments;
}

export function applyOverride(debit: Debit, override: AptOrderOverride | undefined, now: number = Date.now()): Debit {
  if (!override || now - override.at > OVERRIDE_TTL_MS || reflects(debit, override.state)) return debit;
  const { state } = override;
  return {
    ...debit,
    isActive: state.isActive,
    status: state.status,
    statusDate: state.statusDate,
    monthlyAmount: state.monthlyAmount,
    numberOfPayments: state.numberOfPayments,
    endsAt: state.endsAt,
  };
}

export const bankActionUrl = (fundId: number, orderId: number, path: 'freeze' | 'reactivate' | 'cancel' | 'update' | 'actions'): string =>
  `bank-standing-orders/${fundId}/${orderId}/${path}`;

// ---------------------------------------------------------------- the office's lock before the bank file

/*
 * The bank collects from the file the office makes out of APT before the billing day, not from APT,
 * so a change made once the file is made does not reach that collection. The office locks the date
 * first (21.10.2026: «לפני שמשדרים קובץ של 20 לחודש - ננעל את ה-20 לחודש ואז נשדר»), and until it
 * has passed the board offers no change to an order that collects on it. The server refuses the
 * same (AptBankOrderPlanner.LockedCollection); this is so the board says so before anything is sent.
 */

/** The collection dates the office has locked (yyyy-MM-dd), for the fund's board. */
export const bankCollectionLocksUrl = (fundId: number): string => `bank-standing-orders/${fundId}/collection-locks`;

const readIsoDate = (text: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** The given day of the date's month, or the month's last day when it is shorter - as APT collects. */
const billingDayIn = (month: Date, day: number): Date =>
  new Date(month.getFullYear(), month.getMonth(), Math.min(day, new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()));

/**
 * The locked collection that holds the order, or null: a lock still ahead whose date is the order's
 * billing day in that month. A stopped order is held too - it has no next collection of its own, and
 * reactivated after the file was made it would read as live for a collection the file does not carry.
 */
export function lockedCollection(
  order: Pick<NedarimStandingOrderDtoIn, 'billingDay'>,
  lockedDates: readonly string[] | undefined,
  now: Date = new Date(),
): Date | null {
  const day = Number(order.billingDay);
  if (!lockedDates?.length || !Number.isInteger(day) || day < 1 || day > 31) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  let earliest: Date | null = null;
  for (const text of lockedDates) {
    const date = readIsoDate(text);
    if (!date || date <= today || billingDayIn(date, day).getTime() !== date.getTime()) continue;
    if (!earliest || date < earliest) earliest = date;
  }
  return earliest;
}

// ---------------------------------------------------------------- the edit form

export interface BankEditForm {
  amount: string;
  remainingPayments: string;
  unlimited: boolean;
}

export type BankEditField = 'amount' | 'payments';

export type BankEditError = 'amountRequired' | 'amountIncrease' | 'paymentsBelowOne' | 'paymentsTooMany';

export interface BankEditPlan {
  /** Only what changed. Empty when nothing did. */
  request: UpdateBankStandingOrderRequest;
  changes: {
    amount?: { from: number; to: number };
    payments?: { from: number | null; to: number };
  };
  errors: Partial<Record<BankEditField, BankEditError>>;
}

export function initialBankEditForm(order: NedarimStandingOrderDtoIn): BankEditForm {
  const unlimited = order.remainingPayments == null || isUnlimited(order.remainingPayments);
  const remaining = order.remainingPayments ?? 0;
  return {
    amount: String(order.amount),
    remainingPayments: unlimited || remaining <= 0 ? '' : String(remaining),
    unlimited,
  };
}

/** The collections the order has had, returned ones included - each used its turn (as the board counts). */
const collectionsSoFar = (order: NedarimStandingOrderDtoIn): number => order.numberOfBroadcasts ?? 0;

export function planBankEdit(order: NedarimStandingOrderDtoIn, form: BankEditForm): BankEditPlan {
  const plan: BankEditPlan = { request: {}, changes: {}, errors: {} };

  // Amount: down only, as on a credit order (decided 4.10.2026).
  const amountText = form.amount.trim();
  const amount = Number(amountText);
  if (amountText === '' || !Number.isFinite(amount) || amount <= 0) {
    plan.errors.amount = 'amountRequired';
  } else {
    const rounded = Math.round(amount * 100) / 100;
    if (rounded > order.amount) plan.errors.amount = 'amountIncrease';
    else if (rounded !== order.amount) {
      plan.request.amount = rounded;
      plan.changes.amount = { from: order.amount, to: rounded };
    }
  }

  // Payments left, from the next collection: at least one, or until the donor stops.
  const current = order.remainingPayments;
  const currentlyUnlimited = current == null || isUnlimited(current);
  if (form.unlimited) {
    if (!currentlyUnlimited) {
      plan.request.unlimitedPayments = true;
      plan.changes.payments = { from: current, to: UNLIMITED_PAYMENTS };
    }
  } else {
    const text = form.remainingPayments.trim();
    const payments = Number(text);
    const ended = (current ?? 0) <= 0 && !currentlyUnlimited;
    // Empty means "as it is" only where the field opened empty - an ended order, as in the credit form.
    if (text === '' && ended) { /* no change */ }
    else if (text === '' || !Number.isInteger(payments) || payments < 1) plan.errors.payments = 'paymentsBelowOne';
    // APT reads a total of 999 as "until the donor stops".
    else if (collectionsSoFar(order) + payments >= UNLIMITED_PAYMENTS) plan.errors.payments = 'paymentsTooMany';
    else if (currentlyUnlimited || payments !== current) {
      plan.request.remainingPayments = payments;
      plan.changes.payments = { from: currentlyUnlimited ? null : current, to: payments };
    }
  }

  return plan;
}

export const hasBankChanges = (plan: BankEditPlan): boolean => Object.keys(plan.request).length > 0;

export const hasBankErrors = (plan: BankEditPlan): boolean => Object.keys(plan.errors).length > 0;
